import React from "react";
import { InlineIcon, PAPERCLIP_ICON, MIC_ICON, SendIcon } from "./helpers";

export interface ComposeProps {
  draft: string;
  setDraft: (value: string) => void;
  attachFile: File | null;
  setAttachFile: (file: File | null) => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  busy?: boolean;
  sending: boolean;
  selectedAccount: string;
  selected: boolean;
  onSend: (
    e: React.SyntheticEvent,
    opts?: { asVoice?: boolean },
  ) => void | Promise<void>;
}

export function Compose({
  draft,
  setDraft,
  attachFile,
  setAttachFile,
  fileInputRef,
  busy,
  sending,
  selectedAccount,
  selected,
  onSend,
}: ComposeProps) {
  return (
    <form className="ib-chat-compose ib-chat__input-bar" onSubmit={onSend}>
      {attachFile && (
        <div className="ib-compose-attach">
          <span className="ib-compose-attach__chip">
            <InlineIcon d={PAPERCLIP_ICON} size={14} />
            <span className="ib-compose-attach__name">{attachFile.name}</span>
            {attachFile.type ? (
              <span className="ib-muted">{attachFile.type.split("/")[0]}</span>
            ) : null}
          </span>
          <button
            type="button"
            className="ib-link"
            onClick={() => {
              setAttachFile(null);
              if (fileInputRef.current) fileInputRef.current.value = "";
            }}
          >
            Remove
          </button>
        </div>
      )}
      <div className="ib-compose-bar">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/mp4,video/quicktime,video/webm"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0] || null;
            setAttachFile(file);
          }}
        />
        <button
          className={`ib-compose-icon-btn${attachFile ? " ib-compose-icon-btn--active" : ""}`}
          type="button"
          title="Attach photo or video"
          aria-label="Attach photo or video"
          disabled={busy || sending || !selectedAccount || !selected}
          onClick={() => fileInputRef.current?.click()}
        >
          <InlineIcon d={PAPERCLIP_ICON} size={18} />
          {attachFile ? <span className="ib-compose-icon-btn__dot" /> : null}
        </button>
        <div className="ib-compose-input-wrap">
          <textarea
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              const el = e.target;
              el.style.height = "auto";
              el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
            }}
            placeholder={
              attachFile ? "Add an optional caption…" : "Type a message..."
            }
            rows={1}
            disabled={busy || sending || !selectedAccount}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                if (draft.trim() || attachFile) onSend(e);
              }
            }}
          />
        </div>
        <button
          className="ib-compose-icon-btn"
          type="button"
          title="Send as voice note"
          aria-label="Send as voice note"
          disabled={
            busy ||
            sending ||
            !draft.trim() ||
            Boolean(attachFile) ||
            !selectedAccount ||
            !selected
          }
          onClick={(e) => {
            e.preventDefault();
            if (draft.trim() && !attachFile) onSend(e, { asVoice: true });
          }}
        >
          <InlineIcon d={MIC_ICON} size={18} />
        </button>
        <button
          className="ib-compose-send"
          type="submit"
          title={sending ? "Sending…" : "Send"}
          aria-label={sending ? "Sending message" : "Send message"}
          disabled={
            busy ||
            sending ||
            (!draft.trim() && !attachFile) ||
            !selectedAccount
          }
        >
          <SendIcon size={20} />
        </button>
      </div>
    </form>
  );
}
